import React, { useCallback, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useTranslation } from "locales";
import CreateGroupModal from "modals/CreateGroupModal";
import NewChatModal from "modals/NewChatModal";
import useSwipeBack from "hooks/useSwipeBack";
import GroupMessagesList from "./components/GroupMessagesList";
import MessagesList from "./components/MessagesList";
import MessageThreadList from "./components/MessageThreadList";
import MemoryPanel from "components/MemoryPanel";
import useController from "./controller";
import "./style.scss";
import { Routes } from "../../router/config";

function PageMessages() {
  const { t } = useTranslation();
  const { user, setSenderUser, onClickBackButton, match, isSelectThread } = useController();
  const history = useHistory();
  const [showNewChat, setShowNewChat] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showMemory, setShowMemory] = useState(false);

  const handleGroupCreated = useCallback(
    (groupId: string) => {
      history.push(`${Routes.messages}/group/${groupId}`);
    },
    [history]
  );

  const { offset: swipeBackOffset, handlers: swipeBackHandlers } = useSwipeBack({
    enabled: isSelectThread,
    onCommit: onClickBackButton,
  });
  const swipeBackActive = swipeBackOffset > 0;

  if (!user) return <Redirect to={Routes.login} />;

  const cls = ["page-messages"];
  if (!isSelectThread) cls.push("__show-list");
  if (swipeBackActive) cls.push("__swipe-back");

  const containerStyle = swipeBackActive
    ? ({ "--swipe-back-offset": `${swipeBackOffset}px` } as React.CSSProperties)
    : undefined;

  return (
    <div className={cls.join(" ")} style={containerStyle}>
      <div className="page-messages__container">
        <div className="page-messages__sidebar">
          <MessageThreadList
            onNewChat={() => setShowNewChat(true)}
            onCreateGroup={() => setShowCreateGroup(true)}
            onCreateChannel={() => setShowCreateChannel(true)}
          />
        </div>
        <div
          className="page-messages__chat-area"
          {...swipeBackHandlers}
        >
          <Switch>
            <Route exact path={match.url}>
              <div className="page-messages__empty-state">{t("Select a chat to start messaging")}</div>
            </Route>
            <Route path={`${match.url}/group/:groupId/topic/:topicId`}>
              <GroupMessagesList onBack={onClickBackButton} />
            </Route>
            <Route path={`${match.url}/group/:groupId`}>
              <GroupMessagesList onBack={onClickBackButton} />
            </Route>
            <Route path={`${match.url}/:userId`}>
              <MessagesList setSenderUser={setSenderUser} onBack={onClickBackButton} />
            </Route>
          </Switch>
        </div>
      </div>
      <NewChatModal show={showNewChat} onClose={() => setShowNewChat(false)} />
      <CreateGroupModal
        show={showCreateGroup}
        onClose={() => setShowCreateGroup(false)}
        onGroupCreated={handleGroupCreated}
      />
      <CreateGroupModal
        show={showCreateChannel}
        onClose={() => setShowCreateChannel(false)}
        onGroupCreated={handleGroupCreated}
        channelMode
      />
      <button
        type="button"
        className={`page-messages__memory-tab${showMemory ? " __open" : ""}`}
        onClick={() => setShowMemory(true)}
        title={t("Search across all your meetings and chats")}
        aria-label={t("Open Meeting Memory")}
      >
        <span className="page-messages__memory-tab-icon" aria-hidden="true">🧠</span>
      </button>
      <MemoryPanel isOpen={showMemory} onClose={() => setShowMemory(false)} />
    </div>
  );
}

export default PageMessages;
